import React from 'react'
import { useIntl } from 'react-intl'
import { withStyles } from '@mui/styles'
import { FormControl, Typography, FormGroup } from '@mui/material'

import { FormControlLabel, Checkbox } from '@mui/material'

import { checkListInputStyles } from './check-list-input.styles'

/**
 * @typedef {object} CheckListInputOption
 * @property {string | number} key - El valor único de la opción (debe coincidir con cKeyField).
 * @property {string} value - El texto visible de la opción.
 */

/**
 * @typedef {object} CheckListInputChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop name.
 * @property {Array<object>} target.value - Array de objetos con las opciones seleccionadas.
 */

/**
 * @typedef {object} CheckListInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {boolean} [readOnly=false] - Si es true, muestra las opciones seleccionadas como texto de solo lectura.
 * @property {boolean} [required=false] - Si es true, valida que al menos una opción esté seleccionada.
 * @property {Array<object>} [value=[]] - Array de objetos que representan las opciones actualmente seleccionadas.
 * @property {CheckListInputOption[]} [values=[]] - Array de todas las opciones disponibles.
 * @property {string} [cKeyField='key'] - El nombre del campo clave en los objetos del array values.
 * @property {'string' | 'int'} [cKeyType='string'] - El tipo de dato del campo clave.
 * @property {string} [keyField='key'] - El nombre del campo clave en los objetos del array value.
 * @property {string} [valueField='value'] - El nombre del campo valor en los objetos de value.
 * @property {string} [title] - El título o etiqueta que se muestra sobre la lista de checkboxes.
 * @property {string} [name] - El nombre del campo, que se incluirá en el objeto de evento del onChange.
 * @property {'small' | 'normal'} [fontSize] - Controla el tamaño de la fuente del título.
 * @property {function(CheckListInputChangeEvent): void} onChange - La función callback que se ejecuta cuando cambia la selección.
 */

/**
 * Un componente de lista de checkboxes que permite seleccionar múltiples opciones.
 *
 * Muestra una lista vertical de checkboxes donde el usuario puede seleccionar cualquier
 * cantidad de opciones. Incluye validación para campos requeridos, modo de solo lectura,
 * y soporte para diferentes estructuras de datos mediante configuración de campos personalizados.
 *
 * @component
 * @example
 * const MyForm = () => {
 *   const [intereses, setIntereses] = useState([]);
 *
 *   const opciones = [
 *     { key: 1, value: 'Deportes' },
 *     { key: 2, value: 'Música' },
 *     { key: 3, value: 'Lectura' },
 *     { key: 4, value: 'Tecnología' }
 *   ];
 *
 *   const handleChange = (e) => {
 *     console.log(`El campo '${e.target.name}' tiene ${e.target.value.length} seleccionados`);
 *     setIntereses(e.target.value);
 *   };
 *
 *   return (
 *     <CheckListInput
 *       title="Áreas de Interés"
 *       name="intereses"
 *       value={intereses}
 *       values={opciones}
 *       onChange={handleChange}
 *       required
 *       cKeyType="int"
 *     />
 *   );
 * };
 *
 * @param {CheckListInputProps} props - Las props del componente CheckListInput.
 */
export const CheckListInput = withStyles(checkListInputStyles)(({
  classes,
  readOnly = false,
  required = false,
  value = [],
  values = [],
  cKeyField = 'key',
  cKeyType = 'string',
  keyField = 'key',
  valueField = 'value',
  ...props
}) => {
  const { formatMessage } = useIntl()
  const typographyVariant = props.fontSize === 'small' ? '2' : '1'

  const handleChange = (e) => {
    let newValue = [...value]
    if (e.target.checked) {
      newValue.push({
        [keyField]: cKeyType === 'int' ? Number(e.target.name) : e.target.name,
        [valueField]: values.find((v) => v[cKeyField].toString() === e.target.name).value,
      })
    } else if (!e.target.checked) {
      const index = newValue.findIndex((item) => item[keyField].toString() === e.target.name)
      newValue.splice(index, 1)
    }
    props.onChange({ target: { name: props.name, value: newValue } })
  }

  const readOnlyValues = () => {
    let valuesArray = []
    values.forEach((item) => {
      if (value.find((el) => el[keyField].toString() === item[cKeyField].toString()))
        valuesArray.push(item.value)
    })
    return valuesArray
  }

  return (
    <>
      <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
        {props.title}
        {props.required ? <span style={{ color: '#B3272C' }}> *</span> : null}
      </Typography>
      {readOnly ? (
        readOnlyValues().map((item, i) => (
          <Typography key={i} variant={`body${typographyVariant}`} color="textSecondary">
            {item}
          </Typography>
        ))
      ) : (
        <FormControl required={true} error={required && !value.length}>
          <FormGroup>
            {values.map((item, i) => {
              return (
                <FormControlLabel
                  required
                  className={classes.formControlLabel}
                  key={i}
                  control={
                    <Checkbox
                      className={classes.checkbox}
                      color="primary"
                      size="small"
                      checked={
                        value.findIndex(
                          (el) => el[keyField].toString() === item[cKeyField].toString()
                        ) > -1
                      }
                      onChange={handleChange}
                      name={item[cKeyField].toString()}
                    />
                  }
                  label={<Typography variant="body2">{item.value}</Typography>}
                />
              )
            })}
          </FormGroup>
          {required && !value.length ? (
            <Typography variant="caption" color="error">
              {formatMessage({ id: 'checkListInput.required' })}
            </Typography>
          ) : null}
        </FormControl>
      )}
    </>
  )
})
